import Head from 'next/head'
import { useRouter } from 'next/router'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { siteConfig, businessJsonLd } from '@/constants/site'

/** Escapes `<` so the JSON can never close its own script tag. */
const jsonLd = JSON.stringify(businessJsonLd).replace(/</g, '\\u003c')

const absolute = (path: string): string =>
    path.startsWith('/') ? `${siteConfig.url}${path}` : path

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage = siteConfig.ogImage
}): ReactNode => {
    const { asPath } = useRouter()

    const pageTitle = title ? `${title} - ${siteConfig.name}` : `${siteConfig.name} - ${siteConfig.tagline}`
    const path = asPath.split(/[?#]/)[0]
    const pageUrl = `${siteConfig.url}${path === '/' ? '' : path}`
    const imageUrl = ogImage ? absolute(ogImage) : undefined

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={description} />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={description} />
            <meta property='og:type' content='website' />
            <meta property='og:site_name' content={siteConfig.name} />
            <meta property='og:url' content={pageUrl} />
            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={description} />

            {imageUrl && <meta property='og:image' content={imageUrl} />}
            {imageUrl && ogImage === siteConfig.ogImage && siteConfig.ogImageAlt && (
                <meta property='og:image:alt' content={siteConfig.ogImageAlt} />
            )}
            {imageUrl && <meta name='twitter:image' content={imageUrl} />}

            <script
                key='business-jsonld'
                type='application/ld+json'
                dangerouslySetInnerHTML={{ __html: jsonLd }}
            />
        </Head>
    )
}
